import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as practiceUtils from "practiceUtils";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import AskAIView from "views/AskAIView";
import EditableListView from "views/EditableListView";
import ExplanationView from "views/ExplanationView";
import PracticeContextView from "views/PracticeContextView";
import PracticePlayTTSView from "views/PracticePlayTTSView";

interface Props {
  userWord: UserWordStore;
  selectedContextIndex: number;
  onEdit: () => void;
  selectContextIndex: (index: number) => void;
}

interface EditableFields {
  word: string;
  alternativeWords: string[];

  translation: string;
  alternativeTranslations: string[];

  contexts: {
    text: string;
  }[];

  note?: string;
}

interface State {
  initialFields: EditableFields;
  currentFields: EditableFields;
}

export default class PracticeEditView extends preact.Component<Props, State> {
  textareaRef: HTMLTextAreaElement;
  disposeUserWordChange?: () => void;

  constructor(props: Props) {
    super(props);

    const initialFields = {
      word: this.props.userWord.state.value.word,
      alternativeWords: this.props.userWord.state.value.alternativeWords,

      translation: this.props.userWord.state.value.translation,
      alternativeTranslations:
        this.props.userWord.state.value.alternativeTranslations,

      contexts: this.props.userWord.state.value.contexts,

      note: this.props.userWord.state.value.note,
    };

    this.state = {
      initialFields,
      currentFields: { ...initialFields },
    };

    this.disposeUserWordChange = props.userWord.onChange(
      (userWord) => userWord,
      this.forceUpdateCallback
    );
  }

  forceUpdateCallback = () => {
    this.forceUpdate();
  };

  componentWillUnmount() {
    this.disposeUserWordChange?.();
  }

  save() {
    this.props.userWord.save(
      {
        word: this.state.currentFields.word,
        alternativeWords: this.state.currentFields.alternativeWords,
        translation: this.state.currentFields.translation,
        alternativeTranslations:
          this.state.currentFields.alternativeTranslations,
        note: this.state.currentFields.note,
      },
      {
        patch: true,
      }
    );
  }

  undo() {
    this.setState({
      currentFields: { ...this.state.initialFields },
    });
  }

  render() {
    const userWord = this.props.userWord;
    const context =
      this.state.currentFields.contexts[this.props.selectedContextIndex]
        ?.text ?? `No context available for "${userWord.state.value.word}"`;

    return (
      <div class="practiceEditView">
        <div class="topRow">
          <div class="wordPanel">
            <h4>
              {languageMap.map[
                userWord.state.value.language
              ].english.toUpperCase()}
            </h4>
            <div class="alternativeRow">
              <PracticePlayTTSView
                text={userWord.state.value.word}
                language={userWord.state.value.language}
              />
              <input
                class="mainWord"
                type="text"
                value={this.state.currentFields.word}
                onInput={(event) => {
                  this.setState({
                    currentFields: {
                      ...this.state.currentFields,
                      word: event.currentTarget.value,
                    },
                  });
                  this.props.onEdit();
                }}
                lang={userWord.state.value.language}
                dir={
                  languageMap.map[userWord.state.value.language]?.direction ===
                  "RTL"
                    ? "rtl"
                    : undefined
                }
                disabled={
                  true /* doesn't make sense to be able to edit the main word since it would
                  break the link to the contexts */
                }
              />
            </div>
            <EditableListView
              items={this.state.currentFields.alternativeWords}
              onChange={(alternativeWords) => {
                this.setState({
                  currentFields: {
                    ...this.state.currentFields,
                    alternativeWords,
                  },
                });
                this.props.onEdit();
              }}
              language={userWord.state.value.language}
              addButtonText="Add synonym"
            />
          </div>
          <div class="translatePanel">
            <h4>
              {languageMap.map[
                stores.user.firstLanguage.value
              ].english.toUpperCase()}
            </h4>
            <EditableListView
              items={[
                this.state.currentFields.translation,
                ...this.state.currentFields.alternativeTranslations,
              ]}
              onChange={(translations) => {
                this.setState({
                  currentFields: {
                    ...this.state.currentFields,
                    translation: translations[0],
                    alternativeTranslations: translations.slice(1),
                  },
                });

                this.props.onEdit();
              }}
              language={stores.user.firstLanguage.value}
              addButtonText="Add translation"
              preventEmptyList={true}
              autoFocus={deviceDetect.is("mobile") ? false : true}
            />
          </div>
        </div>
        {userWord.state.value.contexts.length > 1 ? (
          <div class="contextHeader">
            <button
              onClick={() =>
                this.props.selectContextIndex(
                  (this.props.selectedContextIndex +
                    userWord.state.value.contexts.length -
                    1) %
                    userWord.state.value.contexts.length
                )
              }
            >
              <i class="icon icon-caret-left"></i>
            </button>
            <h4>
              CONTEXT {this.props.selectedContextIndex + 1}/
              {userWord.state.value.contexts.length}
            </h4>
            <button
              onClick={() =>
                this.props.selectContextIndex(
                  (this.props.selectedContextIndex + 1) %
                    userWord.state.value.contexts.length
                )
              }
            >
              <i class="icon icon-caret-right"></i>
            </button>
          </div>
        ) : (
          <h4>CONTEXT</h4>
        )}
        <p class="context">
          <PracticeContextView
            clozeSentence={practiceUtils.getClozeSentence(
              userWord,
              this.props.selectedContextIndex
            )}
            rtl={
              languageMap.map[userWord.state.value.language]?.direction ===
              "RTL"
            }
            mode={{ type: "complete" }}
          />
        </p>
        {practiceUtils.showContextSource ? (
          <p>
            <small>
              From:{" "}
              {
                userWord.state.value.contexts[this.props.selectedContextIndex]
                  .bookID?.title
              }
            </small>
          </p>
        ) : null}
        <div class="explanation">
          <ExplanationView
            key={[userWord.id, this.props.selectedContextIndex].join("-")}
            word={this.state.currentFields.word}
            context={context}
            language={userWord.state.value.language}
            showInFirstLanguage={true}
            existingTranslations={[
              this.state.currentFields.translation,
              ...this.state.currentFields.alternativeTranslations,
            ]}
            onSelectTranslation={(translation) => {
              this.setState({
                currentFields: {
                  ...this.state.currentFields,
                  alternativeTranslations: [
                    ...this.state.currentFields.alternativeTranslations,
                    translation,
                  ],
                },
              });
              this.props.onEdit();
            }}
          />
        </div>
        {this.state.currentFields.note !== undefined ? (
          <div>
            <h4>NOTE</h4>
            <div class="noteFieldContainer">
              <textarea
                class="noteField"
                onInput={(event) => {
                  this.setState({
                    currentFields: {
                      ...this.state.currentFields,
                      note: event.currentTarget.value,
                    },
                  });
                  this.props.onEdit();
                }}
                ref={(ref) => {
                  this.textareaRef = ref;
                }}
              >
                {this.state.currentFields.note}
              </textarea>
              <button
                class="removeNoteField"
                onClick={() => {
                  this.setState({
                    currentFields: {
                      ...this.state.currentFields,
                      note: undefined,
                    },
                  });
                  this.props.onEdit();
                }}
              >
                <i class="icon icon-remove-sign"></i>
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={async () => {
              await this.setState({
                currentFields: {
                  ...this.state.currentFields,
                  note: "",
                },
              });

              this.textareaRef?.focus();
            }}
          >
            <i class="icon icon-plus"></i> Add note
          </button>
        )}
        {stores.user.hasFeature("askAI") ? (
          <AskAIView userWord={this.props.userWord} context={context} />
        ) : null}
      </div>
    );
  }
}
